import {useCallback, useMemo} from 'react';
import {useSelector, useStore} from 'react-redux';
import type {
  DirectionParam,
  EventStep,
  StepKey,
} from '~/CreateFabricInvitation/components/NavHeader/types';
import {isProOrPartyPassUser} from '~/CreateFabricInvitation/utils/experiments';
import {useEmailPreview} from '~/common/fabric/Card/components/EventPreview/hooks/useEmailPreview';
import {usePreviewInvitation} from '~/common/fabric/Card/components/EventPreview/hooks/usePreviewInvitation';
import {selectGuestCount} from '~/common/fabric/EventGuests/reducers/guests/selectors';
import {selectSavingStep} from '~/common/fabric/reducers/fabricDesign/selectors';
import {UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {UPSELL_MODES} from '~/common/fabric/UpsellModal/UpsellModalContent';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ClickOpenMenu} from '~library/molecules/ClickOpenMenu';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {DiamondFillIcon} from '~library/svg/DiamondFillIcon';
import {ProLogoMobileIcon} from '~library/svg/v2/ProLogoMobileIcon';
import {premiumColor, white} from '~libSass/base/_exports.sass';
import * as styles from './NamedStepsNavWrapper.module.scss';

type HandleModals = {
  toggleUpsellModal: () => void;
  handleCloseUpsell: () => void;
  downgradeBackToBasic: () => void;
  downgradeAndContinue: (route?: string) => Promise<void>;
  upgradeAndContinue: (route?: string) => Promise<void>;
};

type NamedStepsNavCTAsProps = {
  isPremium?: boolean;
  currentRoute: EventStep;
  premiumPreview?: boolean;
  onSave: () => Promise<unknown> | void;
  eventId: string | null;
  preClickHandler: (stepOrDirection: StepKey | DirectionParam) => Promise<void>;
  showUpsellModal: boolean;
  handleModals: HandleModals;
};

export const NamedStepsNavCTAs = ({
  isPremium = false,
  currentRoute,
  premiumPreview = false,
  onSave,
  eventId,
  preClickHandler,
  showUpsellModal,
  handleModals,
}: NamedStepsNavCTAsProps) => {
  const store = useStore();
  const isLargishAndUp = useIsLargishAndUp();
  const guestCount = useSelector(selectGuestCount);
  const isSaving: boolean = useSelector(selectSavingStep);
  const isReviewStep = currentRoute.name === 'preview';
  const nextIsCheckout = currentRoute.nextRoute === 'review';
  const isFinalFreeStep = !isPremium && currentRoute.name === 'preview' && guestCount === 0;
  const isFinalFreeWithGuestsStep = !isPremium && currentRoute.name === 'guests' && guestCount > 0;
  const isFinalFreemiumStep = isPremium && currentRoute.name === 'preview' && nextIsCheckout;
  const isFinalPremiumStep = isPremium && currentRoute.name === 'guests';
  const isFinalStep = useMemo(
    () => isFinalFreeStep || isFinalPremiumStep || isFinalFreemiumStep || isFinalFreeWithGuestsStep,
    [isFinalFreeStep, isFinalPremiumStep, isFinalFreemiumStep, isFinalFreeWithGuestsStep]
  );
  const isCheckoutStep = currentRoute.name === 'review';

  const onPreviewClickHandler = useCallback(
    (callback: (promise: Promise<unknown> | void) => void) => async () => {
      try {
        const savePromise = onSave();
        callback(savePromise);
        if (savePromise instanceof Promise) {
          await savePromise;
        }
      } catch (error) {
        evite.error('Error during preview click handler:', error);
      }
    },
    [onSave]
  );

  const {sendEmailPreview} = useEmailPreview({eventId});
  const {onPreviewInvitationClick} = usePreviewInvitation({eventId});

  const showProLogo = isProOrPartyPassUser(window.subscriptionPlanName);

  const memoizedNextButtonText = useMemo(() => {
    if (typeof currentRoute.nextButtonText === 'function') {
      return currentRoute.nextButtonText(store);
    }
    return currentRoute.nextButtonText || 'Next';
  }, [currentRoute, store]);

  return (
    <div className={styles.namedStepsNavRight}>
      {((currentRoute.previewButtons && !premiumPreview) ||
        isReviewStep ||
        (isCheckoutStep && isLargishAndUp)) && (
        <ClickOpenMenu
          buttonSize={isLargishAndUp ? 'medium' : 'small'}
          title="Preview"
          trigger={
            <Typography
              variant="paragraph2"
              color="charcoal"
              className={styles.namedStepsNavPreview}
            >
              Preview {isLargishAndUp ? <ChevronIcon ratio={0.75} direction="down" /> : null}
            </Typography>
          }
        >
          <li className="clickOpenMenu__li">
            <Button
              variant="transparent"
              className="clickOpenMenu__item"
              data-qa-id="preview-as-guest-btn"
              onClick={onPreviewClickHandler(onPreviewInvitationClick)}
              disableProcessing
            >
              <Typography variant="paragraph2">Preview as guest</Typography>
            </Button>
          </li>
          <li className="clickOpenMenu__li">
            <Button
              variant="transparent"
              className="clickOpenMenu__item"
              data-qa-id="email-preview-btn"
              onClick={onPreviewClickHandler(sendEmailPreview)}
              disableProcessing
            >
              <Typography variant="paragraph2">Email me a preview</Typography>
            </Button>
          </li>
        </ClickOpenMenu>
      )}

      {!isCheckoutStep && (
        <Button
          variant="primary"
          size={isLargishAndUp ? 'medium' : 'small'}
          data-qa-id="next"
          onClick={() => preClickHandler('next')}
          disableProcessing
        >
          <Typography variant="paragraph2" size="medium">
            {memoizedNextButtonText}
          </Typography>{' '}
          {!isFinalStep ? (
            <ArrowIcon direction="right" ratio={isLargishAndUp ? 0.75 : 0.5} color={white} />
          ) : null}
        </Button>
      )}
      {!isLargishAndUp && isCheckoutStep && (isPremium || showProLogo) ? (
        <Typography
          variant="paragraph2"
          color="premiumColor"
          className={styles.namedStepsNavPremiumBadge}
          aria-hidden
          as="span"
        >
          {showProLogo ? (
            <ProLogoMobileIcon />
          ) : (
            <>
              <DiamondFillIcon color={premiumColor} ratio={0.25} /> Premium
            </>
          )}
        </Typography>
      ) : null}

      {showUpsellModal ? (
        <UpsellModal
          noAutoRestore
          toggleUpsellModal={handleModals.toggleUpsellModal}
          closeOverlay={handleModals.handleCloseUpsell}
          onDenyAction={handleModals.downgradeAndContinue}
          onConfirmAction={handleModals.upgradeAndContinue}
          mode={premiumPreview ? UPSELL_MODES.STEP_1_COPY : UPSELL_MODES.UNLOCK_DESIGN_TOOLS}
          cancelCtaText="Dismiss"
          hideSubtitle={!premiumPreview}
          isSaving={isSaving}
        />
      ) : null}
    </div>
  );
};
